/**
 * Firestore Query Utilities
 * Handles Firestore query limitations and optimizations
 */

import { collection, query, where, getDocs, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Chunk an array into smaller arrays of specified size
 * @param {Array} array - Array to chunk
 * @param {number} size - Size of each chunk (default: 10 for Firestore 'in' limit)
 * @returns {Array<Array>} Array of chunks
 */
export function chunkArray(array, size = 10) {
  if (!Array.isArray(array) || array.length === 0) {
    return [];
  }
  
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Limit concurrency of async operations
 * @param {Array<Function>} tasks - Array of async functions
 * @param {number} concurrency - Maximum concurrent operations (default: 5)
 * @returns {Promise<Array>} Results of all tasks
 */
export async function limitConcurrency(tasks, concurrency = 5) {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return [];
  }

  const results = [];
  const executing = [];

  for (const task of tasks) {
    const promise = Promise.resolve(task()).then(result => {
      executing.splice(executing.indexOf(promise), 1);
      return result;
    });

    results.push(promise);
    executing.push(promise);

    if (executing.length >= concurrency) {
      await Promise.race(executing);
    }
  }

  return Promise.all(results);
}

const isIndexBuildError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return (
    message.includes('requires an index') ||
    message.includes('index is currently building') ||
    error?.code === 'failed-precondition'
  );
};

/**
 * Fetch documents by property IDs using chunked queries
 * Handles Firestore's 'in' query limit of 10 items
 * 
 * @param {string} collectionName - Name of the Firestore collection
 * @param {Array<string>} propertyIds - Array of property IDs to query
 * @param {Function} buildQueryForChunk - Function that builds a query for a chunk of property IDs
 *                                        Signature: (collectionRef, propertyIdsChunk) => Query
 * @param {Object} options - Additional options
 * @param {number} options.chunkSize - Size of each chunk (default: 10)
 * @param {number} options.concurrency - Maximum concurrent queries (default: 5)
 * @param {Function} options.mergeResults - Function to merge results from different chunks
 *                                          Default: concatenates arrays
 * @returns {Promise<Array>} Merged and sorted results
 */
export async function fetchByPropertyIds(
  collectionName,
  propertyIds,
  buildQueryForChunk,
  options = {}
) {
  const {
    chunkSize = 10,
    concurrency = 5,
    mergeResults = (results) => results.flat(),
    fallbackBuildQueryForChunk = null,
    postSort = null
  } = options;

  // Early return for empty property IDs
  if (!Array.isArray(propertyIds) || propertyIds.length === 0) {
    return [];
  }

  // Remove duplicates
  const uniquePropertyIds = [...new Set(propertyIds)];

  // If we have 10 or fewer, no need to chunk
  if (uniquePropertyIds.length <= chunkSize) {
    try {
      const collectionRef = collection(db, collectionName);
      const q = buildQueryForChunk(collectionRef, uniquePropertyIds);
      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      return postSort ? postSort(results) : results;
    } catch (error) {
      if (fallbackBuildQueryForChunk && isIndexBuildError(error)) {
        const collectionRef = collection(db, collectionName);
        const fallbackQuery = fallbackBuildQueryForChunk(collectionRef, uniquePropertyIds);
        const snapshot = await getDocs(fallbackQuery);
        const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        return postSort ? postSort(results) : results;
      }
      console.error(`Error fetching ${collectionName} by propertyIds:`, error);
      throw error;
    }
  }

  // Chunk the property IDs
  const chunks = chunkArray(uniquePropertyIds, chunkSize);

  // Build query tasks
  const queryTasks = chunks.map((chunk) => async () => {
    try {
      const collectionRef = collection(db, collectionName);
      const q = buildQueryForChunk(collectionRef, chunk);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      if (fallbackBuildQueryForChunk && isIndexBuildError(error)) {
        try {
          const collectionRef = collection(db, collectionName);
          const fallbackQuery = fallbackBuildQueryForChunk(collectionRef, chunk);
          const snapshot = await getDocs(fallbackQuery);
          return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (fallbackError) {
          console.error(`Error fetching fallback chunk for ${collectionName}:`, fallbackError);
          return [];
        }
      }
      console.error(`Error fetching chunk for ${collectionName}:`, error);
      // Return empty array on error to allow partial results
      return [];
    }
  });

  // Execute queries with concurrency limit
  const results = await limitConcurrency(queryTasks, concurrency);

  // Merge results
  const merged = mergeResults(results);

  // Remove duplicates by document ID (in case same property appears in multiple chunks)
  const uniqueResults = merged.reduce((acc, item) => {
    if (!acc.find(existing => existing.id === item.id)) {
      acc.push(item);
    }
    return acc;
  }, []);

  return postSort ? postSort(uniqueResults) : uniqueResults;
}

/**
 * Fetch viewings by property IDs (with chunking support)
 * @param {Array<string>} propertyIds - Array of property IDs
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of viewing documents
 */
export async function fetchViewingsByPropertyIds(propertyIds, options = {}) {
  const { orderByField = 'startAt', orderDirection = 'asc', concurrency = 5 } = options;
  const uniquePropertyIds = [...new Set((propertyIds || []).filter(Boolean))];
  if (uniquePropertyIds.length === 0) return [];

  const chunks = chunkArray(uniquePropertyIds, 10);
  const tasks = chunks.map((chunk) => async () => {
    const byId = new Map();
    const viewingsRef = collection(db, 'viewings');
    const queryVariants = [
      query(viewingsRef, where('propertyId', 'in', chunk)),
      query(viewingsRef, where('kotID', 'in', chunk)),
      query(viewingsRef, where('kotId', 'in', chunk)),
      query(viewingsRef, where('listingId', 'in', chunk))
    ];

    for (const q of queryVariants) {
      try {
        const snap = await getDocs(q);
        snap.docs.forEach((docSnap) => {
          if (!byId.has(docSnap.id)) {
            const data = docSnap.data();
            byId.set(docSnap.id, {
              id: docSnap.id,
              ...data,
              propertyId: data.propertyId || data.kotID || data.kotId || data.listingId || null
            });
          }
        });
      } catch (error) {
        console.warn('fetchViewingsByPropertyIds variant failed:', error?.message || error);
      }
    }

    return Array.from(byId.values());
  });

  const results = await limitConcurrency(tasks, concurrency);
  const merged = results.flat();
  const deduped = [];
  const seen = new Set();
  merged.forEach((item) => {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      deduped.push(item);
    }
  });

  const dir = orderDirection === 'asc' ? 1 : -1;
  deduped.sort((a, b) => {
    const aDate = a?.[orderByField]?.toDate ? a[orderByField].toDate() : new Date(a?.[orderByField] || 0);
    const bDate = b?.[orderByField]?.toDate ? b[orderByField].toDate() : new Date(b?.[orderByField] || 0);
    return (aDate.getTime() - bDate.getTime()) * dir;
  });
  return deduped;
}

/**
 * Fetch threads/chats by property IDs (with chunking support)
 * @param {Array<string>} propertyIds - Array of property IDs
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of thread/chat documents
 */
export async function fetchThreadsByPropertyIds(propertyIds, options = {}) {
  const { orderByField = 'lastMessageAt', orderDirection = 'desc', concurrency = 5 } = options;
  const uniquePropertyIds = [...new Set((propertyIds || []).filter(Boolean))];
  if (uniquePropertyIds.length === 0) return [];

  const chunks = chunkArray(uniquePropertyIds, 10);
  const tasks = chunks.map((chunk) => async () => {
    const byId = new Map();
    const chatsRef = collection(db, 'chats');
    const queryVariants = [
      query(chatsRef, where('propertyId', 'in', chunk)),
      query(chatsRef, where('kotID', 'in', chunk)),
      query(chatsRef, where('kotId', 'in', chunk)),
      query(chatsRef, where('listingId', 'in', chunk))
    ];

    for (const q of queryVariants) {
      try {
        const snap = await getDocs(q);
        snap.docs.forEach((docSnap) => {
          if (!byId.has(docSnap.id)) {
            const data = docSnap.data();
            byId.set(docSnap.id, {
              id: docSnap.id,
              ...data,
              // Normalize legacy identifiers so downstream filters keep working.
              propertyId: data.propertyId || data.kotID || data.kotId || data.listingId || null
            });
          }
        });
      } catch (error) {
        // Some variants may need indexes; continue with other variants/chunks.
        console.warn('fetchThreadsByPropertyIds variant failed:', error?.message || error);
      }
    }

    return Array.from(byId.values());
  });

  const results = await limitConcurrency(tasks, concurrency);
  const merged = results.flat();
  const deduped = [];
  const seen = new Set();
  merged.forEach((item) => {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      deduped.push(item);
    }
  });

  const dir = orderDirection === 'asc' ? 1 : -1;
  deduped.sort((a, b) => {
    const aDate = a?.[orderByField]?.toDate ? a[orderByField].toDate() : new Date(a?.[orderByField] || 0);
    const bDate = b?.[orderByField]?.toDate ? b[orderByField].toDate() : new Date(b?.[orderByField] || 0);
    return (aDate.getTime() - bDate.getTime()) * dir;
  });
  return deduped;
}

/**
 * Fetch candidate selections by property IDs (with chunking support)
 * @param {Array<string>} propertyIds - Array of property IDs
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of candidate selection documents
 */
export async function fetchCandidateSelectionsByPropertyIds(propertyIds, options = {}) {
  const { orderByField = 'updatedAt', orderDirection = 'desc', statusFilter } = options;

  return fetchByPropertyIds(
    'candidateSelections',
    propertyIds,
    (collectionRef, chunk) => {
      let q = query(
        collectionRef,
        where('propertyId', 'in', chunk)
      );
      
      if (statusFilter) {
        q = query(q, where('status', '==', statusFilter));
      }
      
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }
      
      return q;
    },
    {
      chunkSize: 10,
      concurrency: 5
    }
  );
}

/**
 * Fetch documents using denormalized agencyId field (optimized)
 * This is faster than fetching properties first and using 'in' queries
 * 
 * @param {string} collectionName - Name of the collection
 * @param {string} agencyId - Agency ID to filter by
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of documents
 */
export async function fetchByAgencyId(collectionName, agencyId, options = {}) {
  const {
    clientLinkId = null,
    orderByField = null,
    orderDirection = 'desc',
    limit = null,
    additionalFilters = []
  } = options;

  try {
    let baseQuery = query(
      collection(db, collectionName),
      where('agencyId', '==', agencyId)
    );

  // Add client link filter if provided
    if (clientLinkId) {
      baseQuery = query(baseQuery, where('clientLinkId', '==', clientLinkId));
    }

    // Add additional filters
    for (const filter of additionalFilters) {
      baseQuery = query(baseQuery, where(filter.field, filter.operator, filter.value));
    }

    // Add ordering
    let orderedQuery = baseQuery;
    if (orderByField) {
      orderedQuery = query(orderedQuery, orderBy(orderByField, orderDirection));
    }

    // Add limit
    if (limit) {
      orderedQuery = query(orderedQuery, firestoreLimit(limit));
    }

    try {
      const snapshot = await getDocs(orderedQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      if (orderByField && isIndexBuildError(error)) {
        const snapshot = await getDocs(baseQuery);
        const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sorted = [...results].sort((a, b) => {
          const aDate = a?.[orderByField]?.toDate ? a[orderByField].toDate() : new Date(a?.[orderByField] || 0);
          const bDate = b?.[orderByField]?.toDate ? b[orderByField].toDate() : new Date(b?.[orderByField] || 0);
          const dir = orderDirection === 'asc' ? 1 : -1;
          return (aDate.getTime() - bDate.getTime()) * dir;
        });
        return limit ? sorted.slice(0, limit) : sorted;
      }
      throw error;
    }
  } catch (error) {
    console.error(`Error fetching ${collectionName} by agencyId:`, error);
    throw error;
  }
}

/**
 * Helper to extract property metadata for denormalization
 * @param {Object} property - Property document
 * @returns {Object} Denormalized fields
 */
export function extractPropertyMetadata(property) {
  return {
    agencyId: property.management?.agencyId || property.createdByAgencyId || null,
    clientLinkId: property.management?.linkedClientLinkId || null,
    ownerId:
      property.ownerUid ||
      property.ownerId ||
      property.verhuurderID ||
      property.landlordId ||
      property.landlordID ||
      null,
    propertyId: property.id || null
  };
}

/**
 * Canonical private landlord account for messaging (matches iOS OperatorResolver + Firestore owner fields).
 * Prefer ownerUid so chats line up with the logged-in verhuurder when legacy verhuurderID is stale.
 */
export function canonicalPrivateLandlordUid(property) {
  if (!property) return null;
  return (
    property.ownerUid ||
    property.ownerId ||
    property.verhuurderID ||
    property.landlordId ||
    property.landlordID ||
    null
  );
}

/** Same rules as PropertyDetail / landlord contact: agency inbox when handover is active. */
export function propertyUsesAgencyMessaging(property) {
  if (!property) return false;
  const agencyId = property.management?.agencyId;
  if (!agencyId) return false;
  const handover = property.management?.handoverStatus ?? 'active';
  return ['active', 'requested'].includes(handover);
}

/** UID that should appear in chat participants as the landlord/agency side. */
export function resolveMessagingLandlordUid(property) {
  if (!property) return null;
  if (propertyUsesAgencyMessaging(property)) {
    return property.management?.agencyId || null;
  }
  return canonicalPrivateLandlordUid(property);
}

/**
 * Stable [tenantUid, landlordUid] for chats, given the two party ids from the UI (order-independent).
 */
export function resolveChatParticipantPair(property, user1Id, user2Id) {
  const landlordFromProperty = property ? resolveMessagingLandlordUid(property) : null;
  if (!landlordFromProperty) {
    return {
      tenantUid: user1Id,
      landlordUid: user2Id,
      participants: [user1Id, user2Id]
    };
  }
  const candidates = [user1Id, user2Id].filter(Boolean);
  const tenantUid = candidates.find((u) => u !== landlordFromProperty) ?? user1Id;
  return {
    tenantUid,
    landlordUid: landlordFromProperty,
    participants: [tenantUid, landlordFromProperty]
  };
}

